import { PivotItem } from '@fluentui/react';
import {
  Button,
  buttonStylesGhost,
  buttonStylesPrimary,
  debounce,
  EmptyMessage,
  getDescriptionForItemCount,
  ISearchProps,
  Loader,
  loaderStylesSpinnerXLarge,
  PageHeader,
  pageHeaderStylesSecondary,
  Pivot,
  pivotContainerStylesFill,
  pivotStylesFill,
  Search,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { AccessList } from '../../../components/AccessList/AccessList';
import { DetailHeader, getOwnerNames } from '../../../components/DetailHeader/DetailHeader';
import { FeatureSetList } from '../../../components/FeatureSetList/FeatureSetList';
import { ProjectPanel } from '../../../components/ProjectPanel/ProjectPanel';
import { RegisterFeatureSetPanel } from '../../../components/RegisterFeatureSetPanel/RegisterFeatureSetPanel';
import { fsApi } from '../../../featurestore/api';
import {
  ActivePermission,
  CoreService,
  PermissionType,
  Project,
  ResourceType,
  UserBasicInfo,
  UserWithPermission,
} from '../../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { accessModifierNameMap, ContentsTitle, DetailField, FeatureSetAction } from '../../../featurestore/models';
import { featurestorePageClassName } from '../../../featurestore/styles';
import { getErrorMessageBarMessage, hasAPIError } from '../../../featurestore/utils';
import { useSearchParams } from '../../../utils/hooks';
import { formatDefaultDateAndTime } from '../../../utils/utils';
import { RoutePaths } from '../../Routes';
import { IProjectDetailsClassNames, IProjectDetailsStyles, projectDetailsStylesDefault } from './ProjectDetails.styles';

export interface ProjectWithOwners extends Project {
  owners?: UserBasicInfo[];
  permission?: ActivePermission;
}

const getFields = (data: ProjectWithOwners): DetailField[][] => [
  [
    { label: 'Access Modifier', value: data.accessModifier ? accessModifierNameMap.get(data.accessModifier) : '' },
    { label: 'Latest Update', value: formatDefaultDateAndTime(data.lastUpdateDateTime) },
    { label: 'Created', value: formatDefaultDateAndTime(data.createdDateTime) },
    { label: 'Author', value: data.author?.name },
    { label: 'Owners', value: getOwnerNames(data.owners) },
  ],
];

const canEdit = (data: ProjectWithOwners = {}) =>
  data.permission === ActivePermission.ACTIVE_PERMISSION_OWNER ||
  data.permission === ActivePermission.ACTIVE_PERMISSION_EDITOR;

export const ProjectDetails = () => {
  const history = useHistory();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab');
  const [loadingMessage, setLoadingMessage] = useState<string>('Loading Project');
  const [collapsedHeader, setCollapsedHeader] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [showRegisterFeatureSetPanel, setShowRegisterFeatureSetPanel] = useState(false);
  const [data, setData] = useState<ProjectWithOwners>({});
  const [searchText, setSearchText] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPivotKey, setSelectedPivotKey] = useState(tab ? tab : ContentsTitle.featureSets);
  const changeSearchText = useCallback((text: string) => {
    setSearchText(text);
  }, []);
  const onSearchTextChange = useMemo(() => debounce(300, changeSearchText), [changeSearchText]);
  const onUpdateData = useCallback((count: number, pageSize: number, pageToken: string, name: string) => {
    setDescription(getDescriptionForItemCount(count, pageSize, pageToken, name));
  }, []);
  const { projectId } = useParams<{ projectId: string }>();
  const { addToast } = useToast();
  const load = useCallback(async () => {
    setLoadingMessage(`Loading Project`);
    const results = await Promise.all(
      [
        fsApi(CoreService.GetProjectById, {
          projectId: projectId,
        }),
        fsApi(CoreService.GetActiveProjectPermission, {
          resourceId: projectId,
        }),
        fsApi(CoreService.GetUserProjectPermissions, {
          resourceId: projectId,
          permissionFilter: PermissionType.Owner,
          pageSize: 20,
        }),
      ].map((p) => p.catch((e) => e))
    );
    const resultData: { project: Project; permission?: ActivePermission; users: UserWithPermission[] } = {
      project: {},
      permission: undefined,
      users: [],
    };
    let hasError = false;
    results.forEach((d, i) => {
      if (hasAPIError(d)) {
        addToast(getErrorMessageBarMessage(d, `An error has occurred while loading the project: `));
        hasError = true;
      } else {
        switch (i) {
          case 0:
            resultData.project = d.project;
            break;
          case 1:
            resultData.permission = d.permission;
            break;
          case 2:
            resultData.users = d.users;
        }
      }
    });
    setData({
      ...resultData.project,
      permission: resultData.permission,
      owners: resultData.users.map(({ user = {} }) => user),
    });
    setLoadingMessage('');
    // when failing loading the details, it should go to the list.
    if (hasError) history.push(RoutePaths.FEATURESTORE_PROJECTS);
  }, [addToast, projectId]);
  useEffect(() => {
    load();
  }, [load]);
  const searchProps: ISearchProps = {
    placeholder: 'Search Feature Set by name',
    onSearchTextChange,
  };
  const classNames = useClassNames<IProjectDetailsStyles, IProjectDetailsClassNames>(
    'ProjectDetails',
    projectDetailsStylesDefault,
    undefined,
    featurestorePageClassName
  );
  return !data.id || loadingMessage ? (
    <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
  ) : (
    <div className={classNames.root} data-test="project-details">
      <PageHeader
        styles={pageHeaderStylesSecondary}
        style={{ marginBottom: 24 }}
        pageTitle={data?.name}
        actions={
          canEdit(data) ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button styles={buttonStylesGhost} text="Edit" iconName="Edit" onClick={() => setShowEditPanel(true)} />
              <Button
                styles={buttonStylesPrimary}
                text="Register Feature Set"
                onClick={() => setShowRegisterFeatureSetPanel(true)}
              />
            </div>
          ) : undefined
        }
      />
      <DetailHeader data={data} fields={getFields(data)} collapsed={collapsedHeader} />
      <Pivot
        styles={pivotStylesFill}
        pivotContainerStyles={pivotContainerStylesFill}
        selectedKey={selectedPivotKey}
        data-test="metadata-preview-pivot"
        onLinkClick={(item?: PivotItem) => {
          const title = item?.props.itemKey as ContentsTitle;
          if (title) {
            setSelectedPivotKey(title);
            params.set('tab', title);
            setParams(params);
          }
        }}
        items={[
          {
            key: ContentsTitle.featureSets,
            headerText: ContentsTitle.featureSets,
            content: (
              <div className={classNames.list}>
                <div className={classNames.description}>
                  <div className={classNames.descriptionText}>
                    {data.featureSetsCount && data.featureSetsCount !== '0' ? description : '0 Feature Sets'}
                  </div>
                  <div className={classNames.search}>
                    <Search {...searchProps} />
                  </div>
                </div>
                {data.featureSetsCount && data.featureSetsCount !== '0' ? (
                  <FeatureSetList
                    project={data}
                    searchText={searchText}
                    onUpdateData={onUpdateData}
                    onScroll={(e) => setCollapsedHeader(e.currentTarget.scrollTop !== 0)}
                  />
                ) : (
                  <EmptyMessage message="No Feature Sets Available" />
                )}
              </div>
            ),
          },
          {
            key: ContentsTitle.accessList,
            headerText:
              data.permission === ActivePermission.ACTIVE_PERMISSION_OWNER
                ? ContentsTitle.accessList
                : ContentsTitle.myAccess,
            content: (
              <div className={classNames.list}>
                <AccessList
                  resourceType={ResourceType.PROJECT}
                  id={data.id}
                  name={data.name}
                  activePermission={data.permission}
                  onScroll={(e) => setCollapsedHeader(e.currentTarget.scrollTop !== 0)}
                />
              </div>
            ),
          },
        ]}
      />
      {canEdit(data) ? (
        <>
          <ProjectPanel
            show={showEditPanel}
            onDismiss={(project, deleted) => {
              setShowEditPanel(false);
              if (deleted) {
                history.push(RoutePaths.FEATURESTORE_PROJECTS);
              } else if (project) {
                load();
              }
            }}
            data={data}
          />
          <RegisterFeatureSetPanel
            action={FeatureSetAction.register}
            projectId={data.id}
            projectName={data.name}
            show={showRegisterFeatureSetPanel}
            onDismiss={(updated) => {
              if (updated) {
                history.push(`${RoutePaths.FEATURESTORE_FEATURE_SETS}?tab=${ContentsTitle.ongoingRegistration}`);
              } else {
                setShowRegisterFeatureSetPanel(false);
              }
            }}
          />
        </>
      ) : null}
    </div>
  );
};
